import React from "react";

import Box from "../Box";

import styled from "styled-components";
import BaseDashboardCard from "../BaseDashboardCard";
import useBreakPoint from "../../utils/useBreakPoint";
import { Choice } from "../../types/commercial";
import SelectOption from "../SelectOption";
import { SingleValue } from "react-select";
import { CPopover, CText } from "@carrefour/design-system-components-react";
import { useSearchParams } from "react-router-dom";
import { Flop5 } from "../../types/operational";
import { eventTrack } from "../../utils/gtag";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../utils/numberUtils";
import { useRange } from "../../context/rangeContext";
import { Range } from "../../utils/range";

type Flop5CardProps = {
  title: string;
  listValue: Flop5[];
  isUnavailable: boolean;
  isLoading: boolean;
  openInformationModal: (title?: string) => void;
  range: string;
  options: Choice[];
};

const Text = styled(CText)`
  font-family: system-ui;
`;
const Popover = styled(CPopover)`
  .c-popover__box {
    width: calc(100% + 80px);
  }
  > * .c-popover__header p {
    font-family: system-ui;
    font-size: small;
  }
`;
const TextEllipsis = styled(CText)`
  width: 100%;
  font-family: system-ui;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Table = styled.table`
  width: 100%;
  align-items: center;
  border-collapse: collapse;
`;
const Tr = styled.tr``;
const TrCustom = styled.tr<{ isLast: boolean }>`
  border-collapse: collapse;
  border-bottom: ${(props) => (props?.isLast ? "" : " 1px solid #f0f3f6")};
`;
const Th = styled.th<{ $textAlign?: string; $width?: string }>`
  text-align: ${(props) => props?.$textAlign};
  width: ${(props) => props?.$width};
`;
const Td = styled.td<{ $textAlign?: string }>`
  text-align: ${(props) => props?.$textAlign};
  padding-block: 8px;
`;

const SelectBox = styled(Box)`
  width: 60%;
  @media only screen and (max-width: 600px) {
    width: 100%;
  }
`;
const Flop5Card = ({
  title,
  listValue,
  options,
  range,
  ...res
}: Flop5CardProps) => {
  const isBreak = useBreakPoint();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getKpiRange, setKpiRange } = useRange();

  const getDefaultValueParams = () => {
    const value = getKpiRange(range) ?? options?.[0]?.value as string;
    searchParams.set(range, value );
    setSearchParams(searchParams);
    // tracking chafirstnged
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: value,
    });
    return value;
  };

  const RangeParam = searchParams.get(range) ?? getDefaultValueParams();

  const handleChange = (choice: SingleValue<Choice>) => {
    const newValue = choice?.value as string;

    if (newValue === RangeParam) {
      return;
    }
    searchParams.set(range, newValue as string);
    setSearchParams(searchParams);
    setKpiRange(range, newValue as Range);
    // tracking changed
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: newValue,
    });
  };

  const {t} =useTranslation();

  return (
    <BaseDashboardCard
      hasModal={true}
      title={title}
      option={
        <SelectBox py={1}>
          <SelectOption
            list={options}
            value={RangeParam}
            handleChange={handleChange}
          />
        </SelectBox>
      }
      importedDateTime={listValue?.[0]?.importedDateTime}
      size={"big"}
      {...res}
    >
      <Box display={"flex"} justifyContent={"center"} justifyItems={"center"}>
        <Box
          bg={"white"}
          display={"flex"}
          flexDirection={"column"}
          alignItems="center"
          justifyContent={"center"}
          width="100%"
          borderRadius={1}
          py={2}
        >
          <Table style={{ width: "100%", display: "table" }}>
            <thead style={{ width: "100%", display: "table" }}>
              <Tr>
                <Th $textAlign={"left"} $width={"50%"}>
                  <Box width={["150px", "auto"]}>
                    <Text bold size={"m"} tagStyle={"p"}>
                      {t('operationnel.libelles_ean')}
                    </Text>
                  </Box>
                </Th>
                <Th $textAlign={"center"} $width={"25%"}>
                  <Box width={["70px", "auto"]}>
                    <Text bold size={"m"} tagStyle={"p"}>
                      {t('operationnel.qte')}
                    </Text>
                  </Box>
                </Th>
                <Th
                  $textAlign={isBreak === "phone" ? "right" : "right"}
                  $width={"25%"}
                >
                  <Box width={["70px", "auto"]}>
                    <Text bold size={"m"} tagStyle={"p"}>
                      {t('operationnel.montant')}
                    </Text>
                  </Box>
                </Th>
              </Tr>
            </thead>
            <Box width={"100%"} display={"flex"} flexDirection={"column"}>
              <tbody style={{ width: "100%", display: "table" }}>
                {listValue &&
                  listValue?.length > 0 &&
                  listValue?.map((item, index) => {
                    return subElements(
                      item,
                      isBreak,
                      listValue?.length === index + 1
                    );
                  })}
              </tbody>
            </Box>
          </Table>
        </Box>
      </Box>
    </BaseDashboardCard>
  );
};

function subElements(item: Flop5, isBreak: string, isLast: boolean) {
  return (
    <TrCustom key={"top-rec" + item.barcode} isLast={isLast}>
      <Td $textAlign={"left"} width={"50%"}>
        <Box width={["150px", "auto"]}>
          {isBreak === "phone" ? (
            <Popover
              title={item.itemDesc}
              position={"top-right"}
              buttonContent={
                <TextEllipsis bold size={"m"}>
                  {item.itemDesc}
                </TextEllipsis>
              }
            />
          ) : (
            <TextEllipsis bold size={"m"}>
              {item.itemDesc}
            </TextEllipsis>
          )}
        </Box>
        <Text size={"m"}>{item.barcode}</Text>
      </Td>
      <Td $textAlign={"center"} width={"25%"}>
        <Box width={["70px", "auto"]}>
          <Text>
            {new Intl.NumberFormat("fr-FR", {
              maximumFractionDigits: 1,
              minimumFractionDigits: 0,
            }).format(item.quantity)}
          </Text>
        </Box>
      </Td>
      <Td $textAlign={isBreak === "phone" ? "center" : "right"} width={"25%"}>
        <Box width={["70px", "auto"]}>
          <Text>
            {item.shrinkageAmount
              ? `${formatCurrency(item.shrinkageAmount)}`
              : "-"}
          </Text>
        </Box>
      </Td>
    </TrCustom>
  );
}

export default Flop5Card;
